import { OctTable } from "@oceantech/oceantech-ui";
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  CODE_SUCCESS,
  ERROR_MESSAGE,
  MESSAGE,
} from "../../utils/Constant";
import {
  EVALUATE_STATUS,
  TYPE_MODAL,
} from "../constants/criteriaModal";
import { saveCriteria } from "../services/CriteriaServices";
import { AppContext } from "../../appContext/AppContext";
import { IFile, IViewFile } from "../../models/models";
import {
  handleDownloadFileUtil,
  handleUploadFileUtil,
  handleViewFileUtil,
} from "../../utils/FileUtils";
import ViewFile from "../modals/ViewFile";
import {ICriteriaModalProps} from "../models/CriteriaModels";
import {useCriteriaReportContext} from "../context/CriteriaReportContext";
import {CriteriaModalColumns} from "../columns";
import {
  convertPayload as convertQualityIndicatorPayload,
  divideCalculation,
  multiplicationCalculation,
  plusCalculation,
} from "../helpers/qualityIndicator";
import {LIST_CRITERIA} from "../constants/commons";
import {localStorageItem} from "../../utils/LocalStorage";
import {KEY_LOCALSTORAGE} from "../../auth/core/_consts";

const CriteriaTable = (props: ICriteriaModalProps) => {
  const {
    dataTable,
    setDataTable,
    objectSearch,
    setObjectSearch,
    configTable,
    handleDoubleClick,
    type,
    tableCache,
    setTableCache,
    reload,
    setReload,
  } = props;
  const {
    _83_TIEU_CHI,
    _CHI_SO_CHAT_LUONG,
    _TIEU_CHUAN_CHAT_LUONG,
    _XEP_CAP_CHUYEN_MON_KT,
    _MUC_DO_ATPT
  } = LIST_CRITERIA;
  const { setIsLoading } = useContext(AppContext);
  const {criteriaType, criteria} = useCriteriaReportContext();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [viewFile, setViewFile] = useState<IViewFile>({
    open: false,
    file: null,
  });

  const {org = ""} = localStorageItem.get(KEY_LOCALSTORAGE.ACCESS_TOKEN_DECODE)

  const isSurgicalSafety = criteriaType.value === _MUC_DO_ATPT.value;

  const handleViewFile = useCallback(async (file: IFile) => {
    const viewFile = await handleViewFileUtil(file, setIsLoading);
    if (viewFile) {
      setViewFile(viewFile);
    }
  }, []);

  const handleDownloadFile = useCallback(async (file: IFile) => {
    await handleDownloadFileUtil(file, setIsLoading);
  }, []);

  const handleChangeTable = useCallback(async (
    index: number,
    value: any,
    field: string,
    row: any
  ) => {
    const newDataTable = [...dataTable];

    if (field === "evidence") {
      newDataTable[index][field] = await handleUploadFileUtil(
        value,
        setIsLoading
      );
    } else if (field === "status") {
      newDataTable[index][field] = value
        ? EVALUATE_STATUS.EVALUATED.code
        : EVALUATE_STATUS.NOT_RATE_YET.code;
    } else if (field === "pointSelfAssessment" && isSurgicalSafety) {
      console.log(value, "value");
      dataTable[index][field] = value;
      dataTable[index].passingScore = multiplicationCalculation(
        dataTable[index]?.benchmark,
        value?.multiplier ?? 1,
      );
    } else if (["numerator", "denominator"].includes(field)) {
      newDataTable[index][field] = value;
      newDataTable[index]["scoreDepartment"] = divideCalculation(
        newDataTable[index]?.numerator,
        newDataTable[index]?.denominator
      ).toFixed(2);
    } else if (
      [
        "medicalUseOfCI",
        "adverseDrugEffectsOfCI",
        "relatedToSurgeryOfCI",
        "medicalProcedureOfCI",
        "bloodTransfusionOfCI",
        "othersOfCI",
      ].includes(field)
    ) {
      newDataTable[index][field] = value;
      newDataTable[index]["scoreDepartment"] = plusCalculation([
        newDataTable[index]?.medicalUseOfCI || 0,
        newDataTable[index]?.adverseDrugEffectsOfCI || 0,
        newDataTable[index]?.relatedToSurgeryOfCI || 0,
        newDataTable[index]?.medicalProcedureOfCI || 0,
        newDataTable[index]?.bloodTransfusionOfCI || 0,
        newDataTable[index]?.othersOfCI || 0,
      ]).toFixed(2);
    } else if (
      [
        "suicideNCI",
        "heightFallingNCI",
        "childAbductionNCI",
        "violenceRelatedNCI",
        "explosionNCI",
        "hazardousMaterialLossNCI",
        "othersNCI",
      ].includes(field)
    ) {
      newDataTable[index][field] = value;
      newDataTable[index]["scoreDepartment"] = plusCalculation([
        newDataTable[index]?.suicideNCI || 0,
        newDataTable[index]?.heightFallingNCI || 0,
        newDataTable[index]?.childAbductionNCI || 0,
        newDataTable[index]?.violenceRelatedNCI || 0,
        newDataTable[index]?.explosionNCI || 0,
        newDataTable[index]?.hazardousMaterialLossNCI || 0,
        newDataTable[index]?.othersNCI || 0,
      ]).toFixed(2);
    } else {
      newDataTable[index][field] = value;
    }

    setDataTable(newDataTable);
    const key = objectSearch?.level;
    setTableCache((prev: any) => ({
      ...prev,
      [key]: newDataTable,
    }));

    setTouched((prev) => ({
      ...prev,
      [`${field}_${index}`]: true,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTable, objectSearch?.level, setDataTable, setTableCache, setIsLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      let payload: any[] = []
      switch (criteriaType.value) {
        case _83_TIEU_CHI.value:
          payload = [{
            id: criteria?.id,
            criteriaSubsectionId: criteria?.criteriaId,
            planCriteriaId: criteria?.id,
            pointSelfAssessment: criteria?.pointSelfAssessment,
            conclusionAssessment: criteria?.conclusionAssessment,
            pointUnitAssessment: criteria?.pointUnitAssessment,
            pointInspectorAssessment: criteria?.pointInspectorAssessment,
            status: criteria?.status,
            comment: criteria?.comment,
            fileId: criteria?.fileId,
            fileName: criteria?.fileName,
            planName: criteria?.planName,
            criteriaCode: criteria?.criteriaCode,
            criteriaName: criteria?.criteriaName,
            criteriaId: criteria?.criteriaName,
            subsections: dataTable?.map((item: any) => {
              return {
                criteriaSubsectionId: item?.criteriaSubsectionId,
                planCriteriaId: criteria?.id,
                fileId: item?.evidence?.id,
                fileName: item?.evidence?.name,
                resultDepartment: item?.resultDepartment?.id,
                scoreDepartment: item?.scoreDepartment,
                result: item?.result?.id,
                score: item?.score,
                comment: item?.comment,
                orgId: item.orgId || org,
              };
            })
          }]
          break;
        case _CHI_SO_CHAT_LUONG.value:
          payload = convertQualityIndicatorPayload(dataTable, criteria);
          console.log("payload", payload)
          break;
        default:
          break;
      }
      const { data } = await saveCriteria(payload);
      if (data?.code === CODE_SUCCESS) {
        toast.success("Lưu thành công");
        setReload(!reload);
      }
    } catch (error) {
      console.error("error", error);
      toast.error(ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const isFieldDisabled = () => {
    return type.modalType === TYPE_MODAL.VIEW || type.modalType === TYPE_MODAL.EVALUATE;
  };

  const columns = useMemo(() => CriteriaModalColumns(criteriaType?.value)({
    handleChangeTable,
    handleDoubleClick,
    type,
    isView: isFieldDisabled(),
    touched,
    errors,
    handleViewFile,
    handleDownloadFile,
    criteria,
  }), [
    errors,
    handleChangeTable,
    handleDoubleClick,
    handleDownloadFile,
    handleViewFile,
    touched,
    type,
    criteria
  ]);

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <OctTable
          id="searchCriteria"
          className={`table-background-th spaces h-calc-vh-242`}
          data={dataTable}
          columns={columns}
          searchObject={objectSearch}
          setSearchObject={setObjectSearch}
          fixedColumnsCount={-1}
          noToolbar={true}
          totalPages={configTable.totalPages}
          totalElements={configTable.totalElements}
          numberOfElements={configTable.numberOfElements}
          isShowSelection={false}
          clearToolbar={true}
          noPagination={true}
        />

        {type.modalType !== TYPE_MODAL.VIEW && (
          <div className="position-fixed bottom-0 end-0 p-3 w-100 d-flex justify-content-end border-top">
            <Button className="btn-fill min-w-100px" type="submit">
              Lưu
            </Button>
          </div>
        )}
      </form>

      {viewFile?.open && (
        <ViewFile
          show={viewFile?.open}
          file={viewFile?.file}
          onHide={() => setViewFile({ open: false, file: null })}
        />
      )}
    </div>
  );
};

export default CriteriaTable;
